import React from 'react';
import PropTypes from 'prop-types';
import { getDataFromTree } from '@apollo/client/react/ssr';
import initApollo from './init-Apollo';
import Bugsnag from './bugsnag';
import { getLocaleOnSSR } from './helpers/getLocaleOnSSR';
import { getSSRContextData } from './helpers/ssrContext';
import { getI18nForLang } from './helpers/getI18nForLang';
import { getLocaleFromBrowser } from './helpers/getLocaleFromUrl';
import CONFIG from '../config';

// Get language from request (server) or hostname (client)
function getRequestLanguage(req) {
  if (typeof window === 'undefined' && req) {
    return getLocaleOnSSR(req);
  }

  if (typeof window !== 'undefined') {
    return getLocaleFromBrowser();
  }

  return null;
}

const withApolloClient = App =>
  class Apollo extends React.Component {
    static displayName = 'withApollo(App)';

    static async getInitialProps(ctx) {
      const {
        Component,
        router,
        ctx: { res, AppTree, req }
      } = ctx;

      // Get language from request (server) or hostname (client)
      // This prevents race conditions when multiple SSR requests run concurrently
      const lng = getRequestLanguage(req) || CONFIG.defaultLocale;

      // Get thread-safe i18n instance for this specific language
      // Each language has its own cached instance, preventing cross-request contamination
      const requestI18n = getI18nForLang(lng);

      // Store the request-scoped i18n in context for child components
      ctx.ctx.requestI18n = requestI18n;
      ctx.ctx.lng = lng;

      const { platform } = getSSRContextData(req);

      // Run all GraphQL queries in the component tree
      // and extract the resulting data
      const apollo = initApollo(
        {},
        {
          lang: lng,
          platform
        }
      );

      ctx.ctx.apolloClient = apollo;

      let appProps = {};
      if (App.getInitialProps) {
        appProps = await App.getInitialProps(ctx);
      }

      if (res && res.finished) {
        // When redirecting, the response is finished.
        // No point in continuing to render
        return {};
      }

      // Only run getDataFromTree for pages that use getInitialProps.
      // Pages with getStaticProps/getServerSideProps already include
      // apolloState via extractApolloState — the client hydrates from it.
      // Running getDataFromTree for those pages causes duplicate API
      // requests (the same data is fetched in both getStaticProps and
      // getDataFromTree).
      const hasGetInitialProps = !!Component.getInitialProps;

      if (typeof window === 'undefined' && hasGetInitialProps) {
        try {
          // Run all GraphQL queries
          await getDataFromTree(
            <AppTree
              {...appProps}
              Component={Component}
              router={router}
              apolloClient={apollo}
            />
          );
        } catch (error) {
          // Prevent Apollo Client GraphQL errors from crashing SSR.
          // Handle them in components via the data.error prop:
          // https://www.apollographql.com/docs/react/api/react-apollo.html#graphql-query-data-error
          console.error('Error while running `getDataFromTree`', error);
          Bugsnag.notify(error);
        }
      }

      // Extract query data from the Apollo store
      const apolloState = apollo.cache.extract();

      return {
        ...appProps,
        apolloState,
        lng
      };
    }

    static propTypes = {
      pageProps: PropTypes.object,
      apolloState: PropTypes.object,
      apolloClient: PropTypes.object,
      bridgeState: PropTypes.object,
      lng: PropTypes.string
    };

    constructor(props) {
      super(props);
      // Determine initial language
      let lang = props.lng;
      if (!lang && typeof window !== 'undefined') {
        lang = getLocaleFromBrowser();
      }
      lang = lang || CONFIG.defaultLocale;
      const apolloState = props.pageProps?.apolloState || props.apolloState;
      this.apolloClient = initApollo(apolloState, { lang });
      this.lastApolloState = apolloState;
    }

    render() {
      // During SSR getDataFromTree, reuse the Apollo client from
      // getInitialProps so useQuery hooks share the same populated cache.
      // this.props.apolloClient is only present when rendered via
      // getDataFromTree's <AppTree apolloClient={apollo}>.
      if (typeof window === 'undefined' && this.props.apolloClient) {
        this.apolloClient = this.props.apolloClient;
        return <App {...this.props} apolloClient={this.apolloClient} />;
      }

      const lang =
        typeof window !== 'undefined' ? getLocaleFromBrowser() : this.props.lng;
      const apolloState =
        this.props.pageProps?.apolloState || this.props.apolloState;

      this.apolloClient = initApollo(apolloState, { lang });

      // Merge SSG/SSR data into cache when navigating between pages.
      // On popstate (back/forward) navigation skip the restore entirely so that
      // fetchMore data accumulated during the previous visit is preserved.
      // On forward navigation merge instead of replace so client-side
      // paginated data is not wiped by a partial SSR apolloState.
      if (apolloState && apolloState !== this.lastApolloState) {
        const isPopState = typeof window !== 'undefined' && window.isPopState;
        if (!isPopState) {
          const existingCache = this.apolloClient.cache.extract();
          this.apolloClient.cache.restore({
            ...existingCache,
            ...apolloState,
            ROOT_QUERY: {
              ...existingCache?.ROOT_QUERY,
              ...apolloState?.ROOT_QUERY
            }
          });
        }
        this.lastApolloState = apolloState;
      }

      return <App {...this.props} apolloClient={this.apolloClient} />;
    }
  };

export default withApolloClient;
